/**
 * Status do atendente (spec 015). Duas leituras: o PRÓPRIO status (chip do
 * topo) e o da equipe (lista de transferência, página de Pausas). As duas
 * vivem sob PRESENCE_KEY: o evento realtime presence.changed invalida a
 * família inteira (use-realtime-sync) e o status muda nas outras telas sem
 * recarregar. O refetch de 60s é só o fallback de quando o SSE cai.
 */
import { queryOptions } from '@tanstack/react-query';
import type { PauseReason, Presence } from '@erp/shared';
import { api } from '@/lib/api-client';
import { errorMessage as genericErrorMessage } from '@/features/tickets/ticket-format';

export const PRESENCE_KEY = ['erp', 'presence'] as const;
export const PAUSE_REASONS_KEY = ['erp', 'pause-reasons'] as const;

export function myPresenceQuery() {
  return queryOptions({
    queryKey: [...PRESENCE_KEY, 'me'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<Presence>('/presence/me', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
    refetchInterval: 60_000,
  });
}

/** só quem tem intervalo aberto — quem não aparece está Ausente */
export function teamPresenceQuery() {
  return queryOptions({
    queryKey: [...PRESENCE_KEY, 'team'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<Presence[]>('/presence', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
    refetchInterval: 60_000,
  });
}

/** `activeOnly` = o menu do chip; sem ele, a lista inteira da configuração */
export function pauseReasonsQuery(opts: { activeOnly?: boolean } = {}) {
  return queryOptions({
    queryKey: [...PAUSE_REASONS_KEY, opts.activeOnly ? 'active' : 'all'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<PauseReason[]>('/presence/pause-reasons', {
        query: opts.activeOnly ? { active: 1 } : {},
        signal,
      });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

/** mapa userId → status corrente (quem falta está Ausente) */
export function presenceByUser(list: Presence[] | undefined): Map<number, Presence> {
  return new Map((list ?? []).map((p) => [p.userId, p]));
}

const ERROR_LABELS: Record<string, string> = {
  pause_reason_required: 'Escolha o motivo da pausa.',
  pause_reason_invalid: 'Este motivo de pausa não está mais ativo — recarregue a lista.',
  pause_reason_exists: 'Já existe um motivo com este nome.',
  pause_reason_in_use: 'Motivo já usado em pausas — desative em vez de excluir.',
  presence_changed: 'Seu status acabou de mudar em outra aba — confira e tente de novo.',
  open_interval_not_found: 'Esta pessoa não tem status aberto para encerrar.',
  ended_at_in_future: 'O horário informado está no futuro.',
  ended_at_before_start: 'O horário informado é anterior ao início do status.',
  attendant_paused: 'Esta pessoa está em pausa e não pode receber conversas agora.',
};

export function presenceErrorMessage(e: unknown): string {
  if (e !== null && typeof e === 'object' && 'error' in e) {
    const code = (e as { error: unknown }).error;
    if (typeof code === 'string' && ERROR_LABELS[code]) return ERROR_LABELS[code];
  }
  return genericErrorMessage(e);
}

/** duração desde `since` como "12 min" / "1 h 05" — o contador do chip */
export function elapsedLabel(since: string | Date, now: number): string {
  const minutes = Math.max(0, Math.floor((now - new Date(since).getTime()) / 60_000));
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')}`;
}
